import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Play, Pause, Activity, Users, Mountain } from "lucide-react";

const REFRESH_MS = 30000;

const relTime = (iso) => {
  if (!iso) return "—";
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
};

const STATUS_META = {
  active: { color: "bg-emerald-500", ring: "ring-emerald-200", text: "text-emerald-700", label: "Active now" },
  recent: { color: "bg-mm-gold", ring: "ring-mm-gold/30", text: "text-amber-800", label: "Recent" },
  idle: { color: "bg-red-500", ring: "ring-red-200", text: "text-red-700", label: "Idle" },
};

const TeacherLiveMonitor = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [paused, setPaused] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [countdown, setCountdown] = useState(REFRESH_MS / 1000);
  const timerRef = useRef(null);
  const tickRef = useRef(null);

  const load = async () => {
    setRefreshing(true);
    try {
      const r = await api.get(`/teacher/classes/${id}/live`);
      setData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load live view");
    } finally {
      setRefreshing(false);
      setCountdown(REFRESH_MS / 1000);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Auto-refresh loop
  useEffect(() => {
    if (paused) return;
    timerRef.current = setInterval(load, REFRESH_MS);
    tickRef.current = setInterval(() => setCountdown((s) => Math.max(0, s - 1)), 1000);
    return () => { clearInterval(timerRef.current); clearInterval(tickRef.current); };
    // eslint-disable-next-line
  }, [paused, id]);

  if (!data) return <div className="min-h-screen bg-mm-parchment"><Navbar /><div className="text-center py-20 text-mm-navy/60">Loading live view...</div></div>;

  const cls = data.class;
  const climbers = data.climbers || [];
  const summary = data.summary || { active: 0, recent: 0, idle: 0, assigned: 0 };

  return (
    <div className="min-h-screen bg-mm-parchment bg-topo">
      <Navbar />
      <div className="container mx-auto py-8 max-w-6xl">
        <Link to={`/teacher/classes/${id}`} className="text-sm text-mm-steel font-bold hover:text-mm-navy">← Back to roster</Link>

        <div className="mt-4 flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-steel flex items-center gap-2">
              <Activity size={12} strokeWidth={3} /> Live monitor
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-black text-mm-navy mt-2" data-testid="live-monitor-title">{cls.name}</h1>
            <p className="text-mm-navy/70 mt-1 text-sm">
              Zero PII — showing progress only. Refreshes every 30 seconds.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} disabled={refreshing} className="btn-trail btn-outline-navy text-xs disabled:opacity-50" data-testid="live-refresh-btn">
              <RefreshCw size={12} strokeWidth={2.5} className={refreshing ? "animate-spin" : ""} /> Refresh now
            </button>
            <button
              onClick={() => setPaused((p) => !p)}
              className={`btn-trail text-xs ${paused ? "btn-gold" : "btn-outline-navy"}`}
              data-testid="live-pause-btn"
            >
              {paused ? <><Play size={12} strokeWidth={2.5} /> Resume</> : <><Pause size={12} strokeWidth={2.5} /> Pause</>}
            </button>
            <div className="text-xs text-mm-navy/60 min-w-[70px] text-right" data-testid="live-countdown">
              {paused ? "Paused" : `Next in ${countdown}s`}
            </div>
          </div>
        </div>

        {/* Summary bar */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="live-summary">
          <SummaryCard color="emerald-500" bg="bg-emerald-50 border-emerald-200" label="Active now" value={summary.active} />
          <SummaryCard color="mm-gold" bg="bg-mm-gold/10 border-mm-gold/40" label="Recent" value={summary.recent} />
          <SummaryCard color="red-500" bg="bg-red-50 border-red-200" label="Idle" value={summary.idle} />
          <SummaryCard color="mm-navy" bg="bg-white border-mm-navy/10" label="Assigned" value={summary.assigned} />
        </div>

        {climbers.length === 0 && (
          <div className="mt-8 text-center py-12 border-2 border-dashed border-mm-navy/15 rounded-xl text-mm-navy/60" data-testid="live-empty">
            <Users size={24} className="mx-auto mb-2 text-mm-navy/30" />
            No assigned climbers yet. Head back to the roster to nickname a few slots.
          </div>
        )}

        {/* Climber grid */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="live-climber-grid">
          {climbers.map((c) => {
            const meta = STATUS_META[c.status] || STATUS_META.idle;
            return (
              <div
                key={c.student_id}
                className={`stage-card p-4 border-2 ${c.status === "active" ? "border-emerald-300" : c.status === "recent" ? "border-mm-gold/40" : "border-mm-navy/10"}`}
                data-testid={`live-climber-${c.slot_number}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-mm-steel">Slot {c.slot_number}</div>
                    <div className="font-heading font-bold text-mm-navy truncate">{c.username}</div>
                    {c.nickname && (
                      <div className="text-xs text-mm-navy/60 truncate" data-testid={`live-climber-nickname-${c.slot_number}`}>{c.nickname}</div>
                    )}
                  </div>
                  <span
                    className={`w-3 h-3 rounded-full ${meta.color} ring-4 ${meta.ring} shrink-0 mt-1`}
                    aria-label={meta.label}
                    title={meta.label}
                    data-testid={`live-climber-dot-${c.slot_number}`}
                  />
                </div>

                {c.current_expedition ? (
                  <div className="mt-3 rounded-lg bg-mm-parchment p-3" data-testid={`live-climber-exp-${c.slot_number}`}>
                    <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-mm-steel">
                      <Mountain size={10} strokeWidth={2.5} /> Current
                    </div>
                    <div className="font-heading font-bold text-mm-navy truncate mt-0.5">{c.current_expedition.topic}</div>
                    <div className="mt-2 flex items-center gap-2 text-xs">
                      <span className="font-bold text-mm-navy">{c.current_expedition.current_stage_label}</span>
                      <span className="text-mm-navy/60">·</span>
                      <span className="text-mm-navy/70">{c.current_expedition.completed_count}/{c.current_expedition.total_stages} stages</span>
                    </div>
                    {/* Progress bar */}
                    <div className="mt-2 h-1.5 bg-mm-navy/10 rounded-full overflow-hidden">
                      <div className="h-full bg-mm-gold transition-all" style={{ width: `${(c.current_expedition.completed_count / Math.max(1, c.current_expedition.total_stages)) * 100}%` }} />
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 text-xs text-mm-navy/50 italic">No active expedition</div>
                )}

                <div className="mt-2 flex items-center justify-between text-[11px] text-mm-navy/60">
                  <span className={`${meta.text} font-bold`}>{meta.label}</span>
                  <span data-testid={`live-climber-last-active-${c.slot_number}`}>
                    {c.last_active_at ? relTime(c.last_active_at) : "never"}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const SummaryCard = ({ label, value, bg, color }) => (
  <div className={`rounded-xl border-2 p-3 ${bg}`}>
    <div className="text-[10px] uppercase tracking-widest font-bold text-mm-navy/60">{label}</div>
    <div className="font-heading text-2xl font-black text-mm-navy mt-1">{value}</div>
  </div>
);

export default TeacherLiveMonitor;
